import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  AccessGroupApiResponse,
  AccountCategoryResponse,
  AccountManagementAuthenticationModel,
  AccountManagementClientApiModel,
  AccountManagementInvitationModel,
  AccountManagementUserModel,
  AuditLogEventApiResponse,
  CreateAccessGroupRequest,
  ModifyUserRoleModel,
  RoleApiResponse,
  UpdateAccessGroupRequest,
  UserDetailsResponse,
  UserToAddModel,
} from 'src/app/models/api/accountManagement.model';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { HttpService } from '../http/http.service';
import { FileDownloadModel } from 'src/app/models/api/fileDownload.model';
import { emptyToUndef, extractEmail } from 'src/app/common/stringUtils';

type AuditLogQuery = {
  entityActions?: string;
  search?: string;
  startTime?: string;
  endTime?: string;
  objectTypeCsv?: string;
  objectIdentifier?: string;
  user?: string;
  actorSources?: string;
  sortData?: SortColumn;
  pageSize?: number;
  pageOffset?: number;
};

@Injectable({
  providedIn: 'root',
})
export class AccountManagementHttpService {
  public readonly defaultPageSize: number = 50;

  /**
   * The user management base url
   * @private
   * @readonly
   */
  private readonly _baseUrl: string = 'account';

  constructor(private readonly _httpService: HttpService) {}

  /**
   * Get all users
   * @returns A collection of AccountManagementUserModel
   */
  public getUsers(): Observable<AccountManagementUserModel[]> {
    const url = `${this._baseUrl}/users`;
    return this._httpService.get<AccountManagementUserModel[]>(url);
  }

  public getUsersWithExpandOption(expandOption: 'none' | 'all'): Observable<AccountManagementUserModel[]> {
    const url = `${this._baseUrl}/users`;
    const params = new HttpParams({ fromObject: { expandOption } });
    return this._httpService.get<AccountManagementUserModel[]>(url, undefined, params);
  }

  /**
   * Get all invitations
   * @returns A collection of AccountManagementUserModel
   */
  public getInvitations(): Observable<AccountManagementInvitationModel[]> {
    const url = `${this._baseUrl}/invitations`;
    return this._httpService.get<AccountManagementInvitationModel[]>(url);
  }

  /**
   * Get all authentications
   * @returns A collection of AccountManagementUserModel
   */
  public getAuthentications(): Observable<AccountManagementAuthenticationModel[]> {
    const url = `${this._baseUrl}/authentications`;
    return this._httpService.get<AccountManagementAuthenticationModel[]>(url);
  }

  /**
   * Invite a user
   * @returns invitation id
   */
  public inviteUser(userToAdd: UserToAddModel): Observable<string> {
    const url = `${this._baseUrl}/users/invite`;
    return this._httpService.post<UserToAddModel, string>(url, userToAdd);
  }

  /**
   * Deletes a user
   */
  public deleteUsers(ids: string[]): Observable<string> {
    const url = `${this._baseUrl}/delete-users`;
    return this._httpService.post<string[], string>(url, ids);
  }

  /**
   * Deletes an invitation
   */
  public deleteInvitation(id: string): Observable<string> {
    const url = `${this._baseUrl}/invitations/${id}`;
    return this._httpService.delete<string>(url);
  }

  /**
   * Modify user role
   */
  public modifyRole(userToChange: ModifyUserRoleModel): Observable<string> {
    const url = `${this._baseUrl}/users/roles`;
    return this._httpService.put<string, ModifyUserRoleModel>(url, userToChange);
  }

  /**
   * Get current account api details
   * @returns An instance of AccountManagementClientApiModel
   */
  public apiDetails(): Observable<AccountManagementClientApiModel> {
    const url = `${this._baseUrl}/api`;
    return this._httpService.get<AccountManagementClientApiModel>(url);
  }

  /**
   * Rotate client secret
   * @returns An instance of AccountManagementClientApiModel
   */
  public rotateClientSecret(): Observable<string> {
    const url = `${this._baseUrl}/api/rotate-secret`;
    return this._httpService.post<string, string>(url, '');
  }

  public getAccountDetails$(): Observable<AccountCategoryResponse> {
    const url = `${this._baseUrl}/details`;
    return this._httpService.get<AccountCategoryResponse>(url);
  }

  public getAuditLogEvents(options: AuditLogQuery = {}): Observable<JsonHalCollection<AuditLogEventApiResponse>> {
    return this._fetchAuditLog<JsonHalCollection<AuditLogEventApiResponse>>('audit-log', {
      pageSize: this.defaultPageSize,
      pageOffset: 0,
      ...options,
    });
  }

  public exportAuditLogEvents(
    options: Omit<AuditLogQuery, 'pageSize' | 'pageOffset'> = {}
  ): Observable<FileDownloadModel> {
    return this._fetchAuditLog<FileDownloadModel>('audit-log/export', options);
  }

  private _appendSortQueryParams(httpParams: HttpParams, sortData: SortColumn): HttpParams {
    const sortFieldString: string | undefined = sortData.column.sortKey;
    const sortOrder = sortData.isDesc ? 'desc' : 'asc';

    if (sortFieldString) {
      httpParams = httpParams.append('sortField', sortFieldString);
      httpParams = httpParams.append('sortOrder', sortOrder);
    }

    return httpParams;
  }

  private _buildAuditLogParams(query: AuditLogQuery): HttpParams {
    const raw: Record<string, string | undefined> = {
      entityActions: query.entityActions,
      entityTypes: query.objectTypeCsv,
      metadataSearch: emptyToUndef(query.objectIdentifier ?? query.search),
      actorEmails: emptyToUndef(query.user),
      actorSources: emptyToUndef(query.actorSources),
      startTime: query.startTime,
      endTime: query.endTime,
      pageSize: query.pageSize != null ? String(query.pageSize) : undefined,
      pageOffset: query.pageOffset != null ? String(query.pageOffset) : undefined,
    };

    const fromObject: Record<string, string | string[]> = {};
    for (const [k, v] of Object.entries(raw)) {
      if (v !== undefined) fromObject[k] = v;
    }

    let params = new HttpParams({ fromObject });

    if (query.sortData) {
      params = this._appendSortQueryParams(params, query.sortData);
    }
    return params;
  }

  private _fetchAuditLog<T>(endpoint: string, options: AuditLogQuery): Observable<T> {
    const params = this._buildAuditLogParams(options);
    return this._httpService.get<T>(endpoint, undefined, params);
  }

  // Access Groups API methods

  public getAccessGroups(): Observable<JsonHalCollection<AccessGroupApiResponse>> {
    const url = `${this._baseUrl}/access-groups`;
    return this._httpService.get<JsonHalCollection<AccessGroupApiResponse>>(url);
  }

  public createAccessGroup(request: CreateAccessGroupRequest): Observable<AccessGroupApiResponse> {
    const url = `${this._baseUrl}/access-groups`;
    return this._httpService.post<CreateAccessGroupRequest, AccessGroupApiResponse>(url, request);
  }

  public updateAccessGroup(id: string, request: UpdateAccessGroupRequest): Observable<AccessGroupApiResponse> {
    const url = `${this._baseUrl}/access-groups/${id}`;
    return this._httpService.put<AccessGroupApiResponse, UpdateAccessGroupRequest>(url, request);
  }

  public deleteAccessGroup(id: string): Observable<void> {
    const url = `${this._baseUrl}/access-groups/${id}`;
    return this._httpService.delete<void>(url);
  }

  public getRoles(): Observable<RoleApiResponse[]> {
    const url = `${this._baseUrl}/roles`;
    return this._httpService.get<RoleApiResponse[]>(url);
  }

  public getUserDetails(): Observable<UserDetailsResponse> {
    const url = 'user/details';
    return this._httpService.get<UserDetailsResponse>(url);
  }
}
